/**
 * GenericFilterModal - Modale generica per i filtri con effetto balloon
 * Supporta diversi tipi di filtri per diverse pagine
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  FilterList,
  Search,
  AttachMoney,
  Person,
  CalendarToday,
} from '@mui/icons-material';
// Temporaneamente rimosso per evitare conflitti di versioni
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { it } from 'date-fns/locale/it';

export interface FilterField {
  key: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'date';
  options?: { value: string; label: string }[];
  placeholder?: string;
  icon?: React.ReactNode;
  size?: { xs?: number; sm?: number; md?: number };
}

interface GenericFilterModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  fields: FilterField[];
  values: Record<string, any>;
  onUpdateValue: (key: string, value: any) => void;
  onApply: () => void;
  onClear: () => void;
  applyButtonText?: string;
  clearButtonText?: string;
}

const GenericFilterModal: React.FC<GenericFilterModalProps> = ({
  open,
  onClose,
  title,
  fields,
  values,
  onUpdateValue,
  onApply,
  onClear,
  applyButtonText = 'Applica Filtri',
  clearButtonText = 'Cancella Filtri',
}) => {
  const handleApplyAndClose = () => {
    onApply();
    onClose();
  };

  const handleClearAndClose = () => {
    onClear();
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyAndClose();
    }
  };

  const renderField = (field: FilterField) => {
    const value = values[field.key] || '';
    const size = field.size || { xs: 12, sm: 6 };

    switch (field.type) {
      case 'text':
        return (
          <Grid key={field.key} size={size}>
            <TextField
              fullWidth
              label={field.label}
              value={value}
              onChange={(e) => onUpdateValue(field.key, e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={field.placeholder}
              InputProps={{
                startAdornment: field.icon ? (
                  <InputAdornment position="start">{field.icon}</InputAdornment>
                ) : undefined,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        );

      case 'select':
        return (
          <Grid key={field.key} size={size}>
            <FormControl fullWidth>
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={value}
                onChange={(e) => onUpdateValue(field.key, e.target.value)}
                label={field.label}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {field.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );

      case 'date':
        return (
          <Grid key={field.key} size={size}>
            <TextField
              fullWidth
              type="date"
              label={field.label}
              value={value}
              onChange={(e) => onUpdateValue(field.key, e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: field.icon ? (
                  <InputAdornment position="start">{field.icon}</InputAdornment>
                ) : undefined,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        );

      case 'number':
        return (
          <Grid key={field.key} size={size}>
            <TextField
              fullWidth
              label={field.label}
              type="number"
              value={value}
              onChange={(e) => onUpdateValue(field.key, e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={field.placeholder}
              InputProps={{
                startAdornment: field.icon ? (
                  <InputAdornment position="start">{field.icon}</InputAdornment>
                ) : undefined,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -8,
            right: 24,
            width: 16,
            height: 16,
            backgroundColor: 'white',
            transform: 'rotate(45deg)',
            border: '1px solid rgba(0,0,0,0.1)',
            borderBottom: 'none',
            borderRight: 'none',
            zIndex: 1,
          },
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          pt: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 'auto',
          height: 'auto',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <FilterList color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3, px: 4 }}>
        <Box sx={{ pt: 6 }}>
          <Grid container spacing={3}>
            {fields.map(renderField)}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClearAndClose}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {clearButtonText}
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyAndClose}
          startIcon={<FilterList />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          {applyButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenericFilterModal;
